import { MessageChannel } from "@redotech/cockroachdb-codegen/tables/shopper-ai/message-channel";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import * as z from "zod";

export const DEFAULT_SHOPPER_AGENT_PERSONA_NAME = "Jessica";

export const ShopperAgentPersonaConfigSchema = z.object({
  name: z.string().optional(),
});

export type ShopperAgentPersonaConfig = z.infer<
  typeof ShopperAgentPersonaConfigSchema
>;

/**
 * Resolves the persona name the agent should use when speaking to a customer.
 * Falls back to {@link DEFAULT_SHOPPER_AGENT_PERSONA_NAME} when the team has
 * no persona configured or has cleared the name. All read sites should go
 * through this helper so the default is enforced in one place.
 */
export function getPersonaName(
  config: { name?: string | null } | null | undefined,
): string {
  return config?.name?.trim() || DEFAULT_SHOPPER_AGENT_PERSONA_NAME;
}

/**
 * History reaches the agent as `message_channel` (CRDB) or as
 * `ConversationPlatform` (Mongo support conversations). Both spell the same
 * channels with the same string values, so one set answers for either.
 */
export type PersonaIntroChannel = MessageChannel | ConversationPlatform;

/**
 * Channels the agent sends outreach on, and so the only ones a prior intro
 * could have come from. A channel qualifies only if it is two-way text *and*
 * is us — Attentive and Postscript are text but third-party senders on their
 * own numbers, while `mobileMessage` is our own iMessage pipeline.
 *
 * This decides only whether an intro already happened. Whether the agent may
 * introduce itself is `isTextConcierge` in the vanguard-loop runner; adding a
 * channel here means changing that too.
 */
const PERSONA_INTRO_CHANNELS: ReadonlySet<PersonaIntroChannel> = new Set([
  MessageChannel.Sms,
  MessageChannel.Mobilemessage,
]);

export function canChannelCarryPersonaIntro(
  channel: PersonaIntroChannel | null | undefined,
): boolean {
  return !!channel && PERSONA_INTRO_CHANNELS.has(channel);
}
